import React, { useEffect, useReducer } from 'react'
import * as R from 'ramda'
import {
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Grid,
  Icon,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@material-ui/core'
import effects from 'app/effects/effects'
import clsx from 'clsx'
import CloseIcon from '@material-ui/icons/Close'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import ResponsiveDialogAction from 'app/components/dialog/ResponsiveDialogAction'
import Assets from 'app/configs/assetConfigs'
import assetConfigs from 'app/configs/assetConfigs'
import EditIcon from '@material-ui/icons/Edit'
import moment from 'moment'
import Promise from 'bluebird'
import * as StatusConfigs from 'app/configs/statusConfigs'
import { useAccessControl } from 'app/auth/useAccessControl'
import { AccessType } from 'app/auth/accessControlService'

const useStyle = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: 760,
    },
  },
  titleSection: {
    backgroundColor: '#064e75',
  },
  textGray: {
    color: theme.palette.darkGrey.main,
  },
  veryLight: {
    color: theme.palette.grey.veryLight,
  },
  veryLightBackground: {
    backgroundColor: theme.palette.grey.veryLight,
  },
  secondary: {
    color: theme.palette.secondary.main,
  },
  image: {
    height: 180,
    width: 180,
  },
  listContainer: {
    '& .MuiList-padding': {
      paddingTop: '0px',
      paddingBottom: '0px',
    },
    paddingTop: '0px !important',
    paddingBottom: '0px !important',
    margin: '3.6rem 3.6rem 2.8rem 3.6rem',
    border: '1px solid #cbd5e0',
  },
  listBorder: {
    borderBottom: '2px solid #eee',
  },
  chipStatus: {
    width: '91px',
  },
  chipActive: {
    backgroundColor: theme.palette.active.main,
    color: '#ffffff',
  },
  chipInactive: {
    backgroundColor: theme.palette.grey.veryLight,
    color: '#111',
  },
}))

const GroupCardStatus = {
  INACTIVE: 0,
  ACTIVE: 1,
}
const CardStatus = {
  active: 'Active',
  inactive: 'Inactive',
  suspended: 'Suspended',
  revoked: 'Revoked',
  canceled: 'Canceled',
}

const ActionTypes = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCSS',
  FETCH_ERROR: 'FETCH_ERROR',
}

const initialState = {
  isLoading: false,
  data: [],
  error: null,
}

const SUBSCIBER_STATUS_ALLOW_ADD_MEMBER_CARD = [
  ...StatusConfigs.PLACE_SUBSCRIBED,
  StatusConfigs.PLACE_INVITATION_INVITED,
  StatusConfigs.PLACE_INVITATION_ADDED,
]
const reducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.FETCH_START:
      return { ...state, isLoading: true }
    case ActionTypes.FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        data: action.data,
        cardOptions: action.cardOptions,
      }
    case ActionTypes.FETCH_ERROR:
      return { ...state, isLoading: false, error: action.error }
    default:
      return state ? state : initialState
  }
}

const fetchSubcriberMembershipCard = (subscriberId) =>
  effects.apis.getSubscriberMemberCard(subscriberId)

function avatar(subscriber) {
  const crmAvatar = R.path(['crmInfo', 'avatar'], subscriber)
  const subscriberAvatar = R.path(['avatar'], subscriber)
  if (!R.isNil(crmAvatar)) {
    return crmAvatar
  } else if (!R.isNil(subscriberAvatar)) {
    return subscriberAvatar
  } else {
    return Assets.personSilhouetteImage
  }
}

function displayName(subscriber) {
  const crmFirstName = R.path(['crmInfo', 'firstName'], subscriber)
  const crmLastName = R.path(['crmInfo', 'lastName'], subscriber)
  if (R.all(R.complement(R.isNil), [crmFirstName, crmLastName])) {
    return crmFirstName + ' ' + crmLastName
  }

  return R.compose(
    R.join(' '),
    R.filter(R.complement(R.isNil)),
  )([subscriber.firstName, subscriber.lastName])
}

const getNoCardErrorMessage = (status, membershipCardList, cardOptions) => {
  if (
    [
      StatusConfigs.PLACE_INVITATION_UNSUBSCRIBED,
      StatusConfigs.PLACE_INVITATION_REMOVE_SUBSCRIPTION,
    ].includes(status)
  )
    return 'membershipCard.form.notice.unsubscribed'

  if (
    [
      StatusConfigs.PLACE_INVITATION_IGNORED,
      StatusConfigs.PLACE_INVITATION_DECLINED,
    ].includes(status)
  )
    return 'membershipCard.form.notice.rejected'

  if (StatusConfigs.PLACE_INVITATION_PENDING_REQUEST === status)
    return 'membershipCard.form.notice.requesting'

  if (cardOptions && cardOptions.length <= 0)
    return 'membershipCard.form.noCardsAvailable'

  return 'membershipCard.form.notice.notAssign'
}

const isCardActive = ({ subscrCardStatus, groupCardStatus }) =>
  subscrCardStatus === CardStatus.active.toLowerCase() &&
  groupCardStatus !== GroupCardStatus.INACTIVE

const isShowList = (state) =>
  !state.isLoading && state.data && state.data.length > 0

const isEmpty = (state) =>
  !state.isLoading && state.data && state.data.length <= 0

const fetchMembershipCardOptions = (subscriberId) =>
  effects.apis
    .getMemberCardOptions(subscriberId)
    .then((response) => response.data)

const availableCardOptions = (cardOptions, membershipCardList) => {
  if (!cardOptions || cardOptions.length <= 0) return []

  let usedCardId = R.pluck('groupCardId', membershipCardList)

  return R.compose(
    R.filter(R.o(R.not, R.includes(R.__, usedCardId))),
    R.pluck('id'),
  )(cardOptions)
}

const SubscriberMembershipCardListDialog = (props) => {
  const { subscriber, onClose, openMembershipCardDetailDialog } = props
  const [state, dispatch] = useReducer(reducer, initialState)
  const classes = useStyle()
  const { isGranted: isAutorized } = useAccessControl([
    AccessType.SUBSCRIBER_CREATE,
    AccessType.SUBSCRIBER_UPDATE,
    AccessType.ADMINISTRATION,
  ])

  useEffect(() => {
    dispatch({ type: ActionTypes.FETCH_START })
    Promise.all([
      fetchMembershipCardOptions(subscriber.id),
      fetchSubcriberMembershipCard(subscriber.id),
    ]).then(([cardOptions, membershipCardList]) => {
      dispatch({
        type: ActionTypes.FETCH_SUCCESS,
        data: membershipCardList.data,
        cardOptions: cardOptions,
      })
    })
  }, [subscriber.id, subscriber.refetch])

  return (
    <Dialog
      maxWidth="md"
      className={classes.dialog}
      fullWidth={true}
      open={true}
    >
      <DialogTitle
        disableTypography={true}
        className={clsx('p-24', classes.titleSection)}
      >
        <div className="flex justify-between">
          <div className="flex">
            <div className="relative w-96 h-96">
              <img
                className="absolute w-full h-full rounded-full"
                src={avatar(subscriber)}
                alt="avatarSubscriber"
              />
            </div>
            <div className="ml-32 mb-24 flex-1 flex items-start sm:items-center flex-col sm:flex-row">
              <div className="text-24 text-white font-bold">
                {displayName(subscriber)}
              </div>
              {state.isExpiredBadgeShow && (
                <div
                  className={clsx(
                    'mt-8 sm:mt-0 sm:ml-16 text-14 px-10 py-4 rounded-full',
                    classes.veryLightBackground,
                    classes.textGray,
                  )}
                >
                  <FormattedMessage id="membershipCard.form.status.expired" />
                </div>
              )}
            </div>
          </div>

          <div>
            <IconButton
              color="primary"
              className="p-2 cursor-pointer inline-flex"
              onClick={onClose}
            >
              <CloseIcon className="text-white" />
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <DialogContent className="p-0">
        <div className="p-24 flex items-center">
          <CreditCardIcon className={clsx('h-10 w-10', classes.textGray)} />
          <span className={clsx('ml-3 text-18 font-bold', classes.textGray)}>
            <FormattedMessage id="membershipCard.form.title" />
          </span>
        </div>
        <div className={clsx('border-t', classes.veryLight)}></div>
        {state.isLoading && (
          <div className="h-320 flex justify-center items-center">
            <div className="flex items-center">
              <CircularProgress className="mr-12 h-32 w-32" />
              <span className="font-simibold text-18">
                <FormattedMessage id="membershipCard.form.loading" />
              </span>
            </div>
          </div>
        )}
        {isEmpty(state) && (
          <div className="h-320 w-512 mx-auto flex justify-center items-center text-center">
            <div className="flex flex-col justify-center items-center">
              <img
                className="h-216"
                alt="No membership card available"
                src={assetConfigs.membershipCardIcon.emptyMemberCard}
              />
              <span className="mt-32 font-simibold text-18">
                <FormattedMessage
                  id={getNoCardErrorMessage(
                    subscriber.lastInvitationStatus,
                    state.data,
                    state.cardOptions,
                  )}
                />
              </span>
            </div>
          </div>
        )}
        {isShowList(state) ? (
          <div className="justify-center items-center w-full">
            <Typography className="font-bold mx-36 my-20" variant="h6">
              <FormattedMessage id="membershipCard.form.list.label" />
            </Typography>
            <List className={classes.listContainer}>
              {state.data.map((item) => {
                let itemGroupCardStatus = R.path(['groupCard', 'status'], item)
                return (
                  <ListItem
                    className={clsx(classes.listBorder, 'flex w-full px-36')}
                    key={item.id}
                    divider
                  >
                    <Grid container className="">
                      <Grid xs={8} item className="align-middle my-auto">
                        <Typography
                          className="font-bold justify-start inline-block"
                          variant="body1"
                        >
                          {item.cardName}
                        </Typography>
                        <Typography
                          className="text-gray-500 justify-start inline-block pl-5"
                          variant="body1"
                        >
                          {item.expireDate &&
                            `(${moment(item.expireDate).format('ll')})`}
                        </Typography>
                      </Grid>
                      <Grid xs={4} item className="text-right">
                        <Chip
                          className={clsx(
                            isCardActive({
                              subscrCardStatus: item.status,
                              groupCardStatus: itemGroupCardStatus,
                            })
                              ? classes.chipActive
                              : classes.chipInactive,
                            classes.chipStatus,
                          )}
                          label={
                            item.status &&
                            itemGroupCardStatus !== GroupCardStatus.INACTIVE
                              ? CardStatus[item.status]
                              : 'Inactive'
                          }
                        ></Chip>
                        {isAutorized &&
                          itemGroupCardStatus !== GroupCardStatus.INACTIVE && (
                            <IconButton
                              aria-label="edit"
                              onClick={() => {
                                openMembershipCardDetailDialog(item)
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                      </Grid>
                    </Grid>
                  </ListItem>
                )
              })}
            </List>
          </div>
        ) : null}
        {subscriber &&
          SUBSCIBER_STATUS_ALLOW_ADD_MEMBER_CARD.includes(
            subscriber.lastInvitationStatus,
          ) &&
          availableCardOptions(state.cardOptions, state.data).length > 0 &&
          isAutorized && (
            <div className="pb-36">
              <ResponsiveDialogAction
                EndButton={
                  <Button
                    variant="contained"
                    color="secondary"
                    className="mx-auto block mt-20"
                    onClick={() => {
                      openMembershipCardDetailDialog({})
                    }}
                  >
                    <div className="inline-flex align-middle">
                      <Icon>add</Icon>
                      <FormattedMessage id="membershipCard.form.button.assignCard" />
                    </div>
                  </Button>
                }
              />
            </div>
          )}
      </DialogContent>
    </Dialog>
  )
}

export default SubscriberMembershipCardListDialog
